import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type {SerializedShadowOptions, Textbox, TextStyleDeclaration} from '@postermywall/fabricjs-2';
import {cache, Gradient} from '@postermywall/fabricjs-2';
import {rgbaToHexString, rgbaToRgbaString} from '@Utils/color.util';
import type {TextStyles} from '@PosterWhiteboard/classes/text-styles.class';
import {BOLD_STROKE_WIDTH_FACTOR, DEFAULT_FONT_STYLE, DEFAULT_STROKE_WIDTH, TEXT_OUTLINE_STROKE_WIDTH_FACTOR} from '@PosterWhiteboard/classes/text-styles.class';
import {addFonts, FONT_LOAD_STATUS, FONT_VARIATION, fontsRequestedMap, getFontFamilyNameForVariations, getFontFamilyString, isBoldVariationAvaliableForFont, isFontLoaded, isItalicVariationAvaliableForFont} from '@Libraries/font-library';
import type {TextLineListObject, TextLineStylesObject, TextRunFillGradientObject, TextRunOutlineObject, TextRunStylesObject, TextSelectionStylesSummary, TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import type {TextHorizontalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import {computeLineListOrdinals, getListMarkerText} from '@PosterWhiteboard/items/text-item/text-line-list.library';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import {BULLETS_FONT_FAMILY} from '@/libraries/font-library';
import {EMPTY_TEXT_RUN_STYLES} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import {FillTypes, getLinearGradientOpts, getRadialGradientOpts} from '@PosterWhiteboard/classes/fill.class';
import {normalizeTextStyleRuns} from '@PosterWhiteboard/items/text-item/text-style-runs.class';

/**
 * Translates the semantic run model (`TextStyleRunObject`, see `text-style-runs.types.ts`) to
 * and from fabric's per-character styles map.
 *
 * Compile: every run becomes a fabric style declaration holding only the raw properties the run
 * overrides (faux bold and outline compile to `stroke`/`strokeWidth`, mirroring the whole-box
 * logic in `TextStyles.getTextStyles` / `TextItem.applyTextStroke`), plus the opaque
 * `pmwRunStyles` passenger carrying the semantic object itself. Fabric preserves the passenger
 * through editing, index shifting, and serialization (fork patch, 7.1.0-pmw-64).
 *
 * Harvest: reads ONLY the passenger back out of the styles map — never reverse-engineers
 * semantics from raw fabric values — so foreign per-character keys (the spell checker's
 * `squigglyline`/`textBackgroundColor`) are naturally ignored.
 */

/**
 * Every raw key a compiled run may write. Clearing these (plus the passenger) before applying
 * runs removes stale declarations left by a previous runs state without touching keys owned by
 * other writers (spell check).
 */
const CLEARED_RUN_DECLARATION: TextStyleDeclaration = {
  fontSize: undefined,
  fontFamily: undefined,
  fontStyle: undefined,
  fill: undefined,
  stroke: undefined,
  strokeWidth: undefined,
  isStrokeForBold: undefined,
  underline: undefined,
  linethrough: undefined,
  shadow: undefined,
  pmwRunStyles: undefined,
};

/**
 * Fabric measures text (and keys its styles map) in graphemes; the grapheme array has no
 * un-underscored accessor.
 */
export const getGraphemeCount = (fabricTextbox: Textbox): number => {
  // eslint-disable-next-line no-underscore-dangle -- fabric exposes the grapheme array only as _text
  return fabricTextbox._text.length;
};

export const getTextUpToGrapheme = (fabricTextbox: Textbox, graphemeIndex: number): string => {
  // eslint-disable-next-line no-underscore-dangle -- fabric exposes the grapheme array only as _text
  return fabricTextbox._text.slice(0, graphemeIndex).join('');
};

/**
 * Replaces a grapheme range of an editing textbox through fabric's own editing API instead of
 * splicing the string, so the per-character styles map (and the run passengers it carries)
 * shifts exactly like typed input; the inserted text inherits the style at the insertion point
 * and the caret lands after it. Used by the paste/cut/spell-suggestion paths of TextItem and
 * SlideshowItem text slides.
 */
export const replaceTextboxRangeWithText = (fabricTextbox: Textbox, textToInsert: string, start: number, end: number): void => {
  fabricTextbox.insertChars(textToInsert, undefined, start, end);
  const caretGraphemeIndex = start + fabricTextbox.graphemeSplit(textToInsert).length;
  fabricTextbox.selectionStart = caretGraphemeIndex;
  fabricTextbox.selectionEnd = caretGraphemeIndex;
  if (fabricTextbox.hiddenTextarea) {
    const caretStringIndex = getTextUpToGrapheme(fabricTextbox, caretGraphemeIndex).length;
    fabricTextbox.hiddenTextarea.value = fabricTextbox.text;
    fabricTextbox.hiddenTextarea.selectionStart = caretStringIndex;
    fabricTextbox.hiddenTextarea.selectionEnd = caretStringIndex;
  }
};

export const replaceTextboxSelectionWithText = (fabricTextbox: Textbox, textToInsert: string): void => {
  replaceTextboxRangeWithText(fabricTextbox, textToInsert, fabricTextbox.selectionStart, fabricTextbox.selectionEnd);
};

/**
 * Replaces the textbox's whole text with `newText` while preserving per-character styling: the
 * minimal changed grapheme range (common prefix/suffix) is replaced through fabric's editing API,
 * so run passengers and per-line records shift exactly as if the edit had been typed. This is what
 * lets the plain-textarea surfaces (mobile Edit sheet, bottom-bar text field) keep runs alive even
 * though they only ever hand over the full string.
 */
export const replaceTextboxTextPreservingStyles = (fabricTextbox: Textbox, newText: string): void => {
  if (fabricTextbox.text === newText) {
    return;
  }
  // eslint-disable-next-line no-underscore-dangle -- fabric exposes the grapheme array only as _text
  const oldGraphemes: string[] = fabricTextbox._text;
  const newGraphemes = fabricTextbox.graphemeSplit(newText);
  const maxCommon = Math.min(oldGraphemes.length, newGraphemes.length);
  let prefixLength = 0;
  while (prefixLength < maxCommon && oldGraphemes[prefixLength] === newGraphemes[prefixLength]) {
    prefixLength += 1;
  }
  let suffixLength = 0;
  while (suffixLength < maxCommon - prefixLength && oldGraphemes[oldGraphemes.length - 1 - suffixLength] === newGraphemes[newGraphemes.length - 1 - suffixLength]) {
    suffixLength += 1;
  }
  const insertedText = newGraphemes.slice(prefixLength, newGraphemes.length - suffixLength).join('');
  replaceTextboxRangeWithText(fabricTextbox, insertedText, prefixLength, oldGraphemes.length - suffixLength);
};

/** The selection's bounding box in the textbox's local top-left coordinate space. */
interface GraphemeRangeBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

const getGraphemeRangeBounds = (fabricTextbox: Textbox, start: number, end: number): GraphemeRangeBounds | null => {
  let minLeft = Number.POSITIVE_INFINITY;
  let maxRight = Number.NEGATIVE_INFINITY;
  let minTop = Number.POSITIVE_INFINITY;
  let maxBottom = Number.NEGATIVE_INFINITY;
  const lineTops: number[] = [];
  let runningTop = 0;
  for (let graphemeIndex = start; graphemeIndex < end; graphemeIndex++) {
    // wrapped-line location: __charBounds and line heights are keyed by VISUAL (wrapped) lines
    const {lineIndex, charIndex} = fabricTextbox.get2DCursorLocation(graphemeIndex);
    // eslint-disable-next-line no-underscore-dangle -- fabric exposes measured char boxes only as __charBounds
    const charBounds = fabricTextbox.__charBounds[lineIndex]?.[charIndex];
    if (!charBounds) {
      continue;
    }
    while (lineTops.length <= lineIndex) {
      lineTops.push(runningTop);
      runningTop += fabricTextbox.getHeightOfLine(lineTops.length - 1);
    }
    // eslint-disable-next-line no-underscore-dangle -- fabric's per-line alignment/indent offset has no public accessor
    const lineLeft = fabricTextbox._getLineLeftOffset(lineIndex);
    minLeft = Math.min(minLeft, lineLeft + charBounds.left);
    maxRight = Math.max(maxRight, lineLeft + charBounds.left + charBounds.width);
    minTop = Math.min(minTop, lineTops[lineIndex]);
    maxBottom = Math.max(maxBottom, lineTops[lineIndex] + fabricTextbox.getHeightOfLine(lineIndex));
  }
  if (!Number.isFinite(minLeft) || maxBottom <= minTop) {
    return null;
  }
  return {left: minLeft, top: minTop, width: maxRight - minLeft, height: maxBottom - minTop};
};

/**
 * The run's gradient with the SAME geometry a whole-box fill of that type renders with, computed
 * over the selection's bounding box instead of the box — so a selected word ramps vertically
 * within its own glyph height exactly like the box's gradient does over the box. Styled-char
 * gradient coordinates live in the textbox's top-left local space (fabric translates the render
 * context by −w/2,−h/2 before applying pixel-unit fillers), hence the bbox translation.
 */
const buildGradientRunFill = (fillGradient: TextRunFillGradientObject, bounds: GraphemeRangeBounds): Gradient<'linear'> | Gradient<'radial'> => {
  if (fillGradient.fillType === FillTypes.LINEAR_GRADIENT) {
    const opts = getLinearGradientOpts(bounds.height, fillGradient.fillColor);
    opts.coords = {x1: bounds.left, y1: (opts.coords?.y1 ?? 0) + bounds.top, x2: bounds.left, y2: (opts.coords?.y2 ?? 0) + bounds.top};
    return new Gradient(opts);
  }
  const opts = getRadialGradientOpts(bounds.width, bounds.height, fillGradient.fillColor);
  opts.coords = {
    r1: opts.coords?.r1 ?? 0,
    r2: opts.coords?.r2 ?? 1,
    x1: (opts.coords?.x1 ?? 0) + bounds.left,
    y1: (opts.coords?.y1 ?? 0) + bounds.top,
    x2: (opts.coords?.x2 ?? 0) + bounds.left,
    y2: (opts.coords?.y2 ?? 0) + bounds.top,
  };
  return new Gradient(opts);
};

/**
 * Writes each gradient run's fabric Gradient onto its characters. This is the ONLY writer of a
 * gradient run's fill: the run's compiled declaration carries no `fill`, and this pass scans the
 * whole styles map for contiguous equal-`fillGradient` passengers — so a partial re-apply that
 * touches a fragment of a run still spans the run's full extent. Runs AFTER initDimensions, since
 * the geometry needs the measured layout; a faux-bold gradient run strokes with the same gradient
 * (the stroke paints in the fill color).
 */
const applyGradientRunFills = (fabricTextbox: Textbox): void => {
  const graphemeCount = getGraphemeCount(fabricTextbox);
  if (graphemeCount === 0) {
    return;
  }
  // Textbox.initDimensions clears the measurement cache without remeasuring (its width is fixed),
  // so force the per-character boxes to exist before reading geometry from them.
  // eslint-disable-next-line no-underscore-dangle -- fabric exposes the measurement cache only as __charBounds
  if (fabricTextbox.__charBounds.length === 0) {
    fabricTextbox.calcTextWidth();
  }
  const declarations: TextStyleDeclaration[] = fabricTextbox.getSelectionStyles(0, graphemeCount);
  let runStart = -1;
  let runGradient: TextRunFillGradientObject | null = null;
  const flushRun = (runEnd: number): void => {
    if (runGradient === null || runStart < 0) {
      return;
    }
    const bounds = getGraphemeRangeBounds(fabricTextbox, runStart, runEnd);
    if (!bounds) {
      return;
    }
    const gradientFill = buildGradientRunFill(runGradient, bounds);
    const charStyles: TextStyleDeclaration = declarations[runStart].isStrokeForBold === true ? {fill: gradientFill, stroke: gradientFill} : {fill: gradientFill};
    fabricTextbox.setSelectionStyles(charStyles, runStart, runEnd);
  };
  for (let graphemeIndex = 0; graphemeIndex < graphemeCount; graphemeIndex++) {
    const gradient = getRunStylesPassenger(declarations[graphemeIndex])?.fillGradient ?? null;
    if (!isEqual(gradient, runGradient)) {
      flushRun(graphemeIndex);
      runStart = gradient ? graphemeIndex : -1;
      runGradient = gradient;
    }
  }
  flushRun(graphemeCount);
};

export const applyStyleRunsToFabricTextbox = (fabricTextbox: Textbox, runs: readonly TextStyleRunObject[], baseStyles: TextStyles): void => {
  const graphemeCount = getGraphemeCount(fabricTextbox);
  if (graphemeCount === 0 || (runs.length === 0 && !stylesMapHasRunDeclarations(fabricTextbox))) {
    return;
  }

  fabricTextbox.setSelectionStyles(CLEARED_RUN_DECLARATION, 0, graphemeCount);
  for (const run of runs) {
    const start = Math.min(run.start, graphemeCount);
    const end = Math.min(run.end, graphemeCount);
    if (start < end) {
      fabricTextbox.setSelectionStyles(compileTextRunStylesToFabricDeclaration(run.styles, baseStyles), start, end);
    }
  }
  // Per-character sizes and families change the wrap layout; setSelectionStyles alone does not
  // re-measure.
  fabricTextbox.initDimensions();
  applyGradientRunFills(fabricTextbox);
};

export const harvestStyleRunsFromFabricTextbox = (fabricTextbox: Textbox): TextStyleRunObject[] => {
  const graphemeCount = getGraphemeCount(fabricTextbox);
  if (graphemeCount === 0) {
    return [];
  }

  const declarations: TextStyleDeclaration[] = fabricTextbox.getSelectionStyles(0, graphemeCount);
  const runs: TextStyleRunObject[] = [];
  for (let graphemeIndex = 0; graphemeIndex < declarations.length; graphemeIndex++) {
    const passenger = getRunStylesPassenger(declarations[graphemeIndex]);
    if (passenger === null) {
      continue;
    }
    const previous = runs[runs.length - 1];
    if (previous?.end === graphemeIndex && isEqual(previous.styles, passenger)) {
      previous.end = graphemeIndex + 1;
    } else {
      runs.push({start: graphemeIndex, end: graphemeIndex + 1, styles: cloneDeep(passenger)});
    }
  }
  return normalizeTextStyleRuns(runs);
};

/**
 * Projects the model's per-line styles onto fabric's `lineStyles` record: `textAlign` is the
 * raw key fabric consumes; the whole model record rides along as the `pmwLineStyles` passenger,
 * which the fork's editing logic shifts/splits/merges with its line and {@link
 * harvestLineStylesFromFabricTextbox} reads back — the per-line mirror of the run passenger.
 */
export const applyLineStylesToFabricTextbox = (fabricTextbox: Textbox, lineStyles: Record<string, TextLineStylesObject>): void => {
  const listOrdinals = computeLineListOrdinals(lineStyles);
  const fabricLineStyles: Record<string, Record<string, unknown>> = {};
  for (const [lineIndex, lineStyleObject] of Object.entries(lineStyles)) {
    const declaration: Record<string, unknown> = {pmwLineStyles: cloneDeep(lineStyleObject)};
    if (lineStyleObject.textAlign !== null) {
      declaration.textAlign = lineStyleObject.textAlign;
    }
    const list = lineStyleObject.list;
    if (list !== null) {
      declaration.listMarker = getListMarkerText(list, listOrdinals[lineIndex] ?? 1);
      if (list.fill !== null) {
        declaration.listMarkerFill = rgbaToRgbaString(list.fill);
      }
      if (list.type === LIST_TYPES.UNORDERED) {
        declaration.listMarkerFontFamily = BULLETS_FONT_FAMILY;
      }
      if (list.indent !== null) {
        declaration.leftIndent = list.indent;
      }
    }
    fabricLineStyles[lineIndex] = declaration;
  }
  fabricTextbox.set('lineStyles', fabricLineStyles);
};

/** A restyled row keeps the explicit gutter it was migrated with, so its wrapping never shifts. */
export const withExistingIndent = (list: TextLineListObject, existingList: TextLineListObject | null): TextLineListObject => {
  return {...list, indent: existingList?.indent ?? list.indent};
};

/**
 * Sets (or clears, list null) list membership on every real line the selection touches — a
 * collapsed caret works on its own line. Ordered ordinals recompute across the whole record, so
 * the projection rebuilds all line declarations.
 */
export const applyListToTextboxSelection = (fabricTextbox: Textbox, list: TextLineListObject | null, selectionStart: number, selectionEnd: number): void => {
  let lineStyles = harvestLineStylesFromFabricTextbox(fabricTextbox);
  const firstLine = fabricTextbox.get2DCursorLocation(selectionStart, true).lineIndex;
  const lastLine = fabricTextbox.get2DCursorLocation(Math.max(selectionStart, selectionEnd - 1), true).lineIndex;
  for (let lineIndex = firstLine; lineIndex <= lastLine; lineIndex++) {
    const existing: TextLineStylesObject | undefined = lineStyles[lineIndex];
    const nextEntry: TextLineStylesObject = {
      textAlign: existing?.textAlign ?? null,
      list: list ? withExistingIndent(list, existing?.list ?? null) : null,
    };
    if (nextEntry.textAlign === null && nextEntry.list === null) {
      lineStyles = withoutLineRecord(lineStyles, lineIndex);
    } else {
      lineStyles[lineIndex] = nextEntry;
    }
  }
  applyLineStylesToFabricTextbox(fabricTextbox, lineStyles);
};

/**
 * Recomputes the rendered list markers from the textbox's own (already index-shifted) line
 * records. fabric's Enter handling duplicates the split row's raw `listMarker` string, so until
 * the debounced commit re-projects the model, a new row would show the previous row's ordinal —
 * the edit-session text-change handlers call this synchronously instead.
 */
export const refreshLineListMarkersOnTextbox = (fabricTextbox: Textbox): void => {
  pruneLineStylesBeyondText(fabricTextbox);
  const lineStyles = harvestLineStylesFromFabricTextbox(fabricTextbox);
  if (Object.keys(lineStyles).length === 0) {
    return;
  }
  applyLineStylesToFabricTextbox(fabricTextbox, lineStyles);
};

const getRealLineCount = (fabricTextbox: Textbox): number => (fabricTextbox.text ?? '').split('\n').length;

/**
 * Fabric's own line-record shifting drops the records of lines a deletion removes — except on
 * the one path where the hidden textarea becomes empty: there it wipes the character styles and
 * returns early, so records for lines 1..n outlived a select-all delete and later re-attached
 * to lines typed afterwards. Records past the real line count have no line to belong to.
 */
const pruneLineStylesBeyondText = (fabricTextbox: Textbox): void => {
  const records = fabricTextbox.lineStyles ?? {};
  const kept = getLineStylesClampedToTextbox(records, fabricTextbox);
  if (Object.keys(kept).length !== Object.keys(records).length) {
    fabricTextbox.lineStyles = kept;
  }
};

/**
 * The records that have a line to belong to: a record for a line index at or past the textbox's
 * real line count is dropped. Pasted styles carry the source item's records, so without this a
 * record for a line the target does not have would be saved and re-attach to a line typed later.
 */
export const getLineStylesClampedToTextbox = <T>(records: Record<string, T>, fabricTextbox: Textbox): Record<string, T> => {
  const lineCount = getRealLineCount(fabricTextbox);
  return Object.fromEntries(Object.entries(records).filter(([lineIndex]) => Number(lineIndex) < lineCount));
};

const withoutLineRecord = (records: Record<string, TextLineStylesObject>, lineIndex: number): Record<string, TextLineStylesObject> => {
  return Object.fromEntries(Object.entries(records).filter(([recordLineIndex]) => Number(recordLineIndex) !== lineIndex));
};

export const harvestLineStylesFromFabricTextbox = (fabricTextbox: Textbox): Record<string, TextLineStylesObject> => {
  const harvested: Record<string, TextLineStylesObject> = {};
  const lineCount = getRealLineCount(fabricTextbox);
  for (const [lineIndex, declaration] of Object.entries(fabricTextbox.lineStyles ?? {})) {
    const passenger = getLineStylesPassenger(declaration);
    if (passenger !== null && Number(lineIndex) < lineCount) {
      harvested[lineIndex] = cloneDeep(passenger);
    }
  }
  return harvested;
};

/**
 * Overrides the alignment of every real line the selection touches — a collapsed caret aligns
 * its own line, matching standard editor behavior. Writes the raw `textAlign` fabric renders
 * with and the updated passenger in one new record (fabric caches invalidate on the `set`).
 */
export const applyAlignmentToTextboxSelection = (fabricTextbox: Textbox, textAlign: TextHorizontalAlignType, selectionStart: number, selectionEnd: number): void => {
  const lineStyles = harvestLineStylesFromFabricTextbox(fabricTextbox);
  const firstLine = fabricTextbox.get2DCursorLocation(selectionStart, true).lineIndex;
  const lastLine = fabricTextbox.get2DCursorLocation(Math.max(selectionStart, selectionEnd - 1), true).lineIndex;
  for (let lineIndex = firstLine; lineIndex <= lastLine; lineIndex++) {
    lineStyles[lineIndex] = {
      textAlign,
      list: lineStyles[lineIndex]?.list ?? null,
    };
  }
  applyLineStylesToFabricTextbox(fabricTextbox, lineStyles);
};

const getLineStylesPassenger = (declaration: Record<string, unknown> | undefined): TextLineStylesObject | null => {
  const passenger: unknown = declaration?.pmwLineStyles;
  if (typeof passenger === 'object' && passenger !== null && 'textAlign' in passenger) {
    return passenger as TextLineStylesObject;
  }
  return null;
};

export const compileTextRunStylesToFabricDeclaration = (runStyles: TextRunStylesObject, baseStyles: TextStyles): TextStyleDeclaration => {
  const declaration: TextStyleDeclaration = {
    pmwRunStyles: {...cloneDeep(runStyles)},
  };

  if (runStyles.fontSize !== null) {
    declaration.fontSize = runStyles.fontSize;
  }
  if (runStyles.underLine !== null) {
    declaration.underline = runStyles.underLine;
  }
  if (runStyles.lineThrough !== null) {
    declaration.linethrough = runStyles.lineThrough;
  }
  if (runStyles.fillColor !== null && runStyles.fillGradient === null) {
    declaration.fill = rgbaToRgbaString(runStyles.fillColor);
  }
  if (runStyles.shadow !== null) {
    // The raw render subset; the full serialized shadow (type preset included) rides in the passenger.
    declaration.shadow = {
      color: runStyles.shadow.color,
      blur: runStyles.shadow.blur,
      offsetX: runStyles.shadow.offsetX,
      offsetY: runStyles.shadow.offsetY,
    };
  }

  applyCompiledFontVariation(declaration, runStyles, baseStyles);
  applyCompiledStroke(declaration, runStyles, baseStyles);

  return declaration;
};

/**
 * Mirrors `TextStyles.getTextStyles`: bold/italic resolve to a font-family variation when the
 * font ships one, and italic falls back to the synthetic `fontStyle` when it does not. Emitted
 * only when the run overrides any of family/bold/italic; the effective values resolve nulls
 * against the whole-box base.
 */
const applyCompiledFontVariation = (declaration: TextStyleDeclaration, runStyles: TextRunStylesObject, baseStyles: TextStyles): void => {
  if (runStyles.fontFamily === null && runStyles.isBold === null && runStyles.isItalic === null) {
    return;
  }

  const fontFamily = runStyles.fontFamily ?? baseStyles.fontFamily;
  const isBold = runStyles.isBold ?? baseStyles.isBold;
  const isItalic = runStyles.isItalic ?? baseStyles.isItalic;
  const fontFamilyWithVariation = getFontFamilyNameForVariations(fontFamily, isBold, isItalic);
  const loadedFontStatus: number = FONT_LOAD_STATUS.LOADED;
  const isFontLoaded = fontsRequestedMap[fontFamilyWithVariation] === loadedFontStatus;
  const isItalicApplied = isFontLoaded ? isItalicVariationAvaliableForFont(fontFamily) : false;

  declaration.fontFamily = getFontFamilyString(fontFamilyWithVariation);
  declaration.fontStyle = isItalic && !isItalicApplied ? FONT_VARIATION.ITALIC : DEFAULT_FONT_STYLE;
};

/**
 * A run's visible stroke is the effective outline when one is enabled, otherwise the effective
 * faux-bold stroke (bold without a real bold font variation), mirroring `TextItem.applyTextStroke`
 * and the bold branch of `TextStyles.getTextStyles`. `isStrokeForBold` marks the faux-bold case
 * so the raw declaration stays distinguishable from an outline in fabric's serialized styles.
 *
 * Stroke keys are emitted whenever ANY stroke-affecting property is overridden — not only
 * bold/outline. A faux-bold stroke is drawn in the fill color and sized by the font, so a run
 * that overrides only fill/size/family under a bold or outlined base must still recompile its
 * stroke; inheriting the object-level stroke there paints the base color over the run's fill.
 */
const applyCompiledStroke = (declaration: TextStyleDeclaration, runStyles: TextRunStylesObject, baseStyles: TextStyles): void => {
  if (runStyles.outline === null && runStyles.isBold === null && runStyles.fillColor === null && runStyles.fillGradient === null && runStyles.fontSize === null && runStyles.fontFamily === null) {
    return;
  }

  const fontSize = runStyles.fontSize ?? baseStyles.fontSize;
  const effectiveOutline: TextRunOutlineObject = runStyles.outline ?? {isEnabled: baseStyles.stroke, color: baseStyles.strokeColor, width: baseStyles.strokeWidth};

  if (effectiveOutline.isEnabled) {
    declaration.stroke = rgbaToHexString(effectiveOutline.color);
    declaration.strokeWidth = fontSize * effectiveOutline.width * TEXT_OUTLINE_STROKE_WIDTH_FACTOR;
    declaration.isStrokeForBold = false;
    return;
  }

  const isBold = runStyles.isBold ?? baseStyles.isBold;
  const fontFamily = runStyles.fontFamily ?? baseStyles.fontFamily;
  if (isBold && !isBoldVariationAvaliableForFont(fontFamily)) {
    const fillColor = runStyles.fillColor ?? baseStyles.fill.fillColor[0];
    declaration.stroke = rgbaToRgbaString(fillColor);
    declaration.strokeWidth = BOLD_STROKE_WIDTH_FACTOR * fontSize;
    declaration.isStrokeForBold = true;
    return;
  }

  declaration.stroke = null;
  declaration.strokeWidth = DEFAULT_STROKE_WIDTH;
  declaration.isStrokeForBold = false;
};

/**
 * Merges per-grapheme semantic style overrides into the selection's existing run passengers and
 * recompiles the affected declarations. Adjacent graphemes whose merged passenger is deep-equal
 * are written as one `setSelectionStyles` segment.
 */
export const applyPerGraphemeRunStylesToTextboxSelection = (
  fabricTextbox: Textbox,
  baseStyles: TextStyles,
  selectionStart: number,
  perGraphemeStyles: Partial<TextRunStylesObject>[]
): void => {
  if (perGraphemeStyles.length === 0) {
    return;
  }

  const declarations: TextStyleDeclaration[] = fabricTextbox.getSelectionStyles(selectionStart, selectionStart + perGraphemeStyles.length);
  const mergedPassengers = declarations.map((declaration, graphemeOffset) => ({
    ...EMPTY_TEXT_RUN_STYLES,
    ...(getRunStylesPassenger(declaration) ?? {}),
    ...perGraphemeStyles[graphemeOffset],
  }));

  let segmentStart = 0;
  for (let graphemeOffset = 1; graphemeOffset <= mergedPassengers.length; graphemeOffset++) {
    if (graphemeOffset === mergedPassengers.length || !isEqual(mergedPassengers[graphemeOffset], mergedPassengers[segmentStart])) {
      fabricTextbox.setSelectionStyles(compileTextRunStylesToFabricDeclaration(mergedPassengers[segmentStart], baseStyles), selectionStart + segmentStart, selectionStart + graphemeOffset);
      segmentStart = graphemeOffset;
    }
  }
  fabricTextbox.initDimensions();
  applyGradientRunFills(fabricTextbox);
};

export const applyRunStylesToTextboxSelection = (fabricTextbox: Textbox, stylesPartial: Partial<TextRunStylesObject>, baseStyles: TextStyles, selectionStart: number, selectionEnd: number): void => {
  applyPerGraphemeRunStylesToTextboxSelection(fabricTextbox, baseStyles, selectionStart, new Array<Partial<TextRunStylesObject>>(selectionEnd - selectionStart).fill(stylesPartial));
};

/**
 * The effective (run override ?? whole-box base) style of every grapheme the current selection
 * covers — for a collapsed caret, the style typing would continue with. `null` per property =
 * the selection is mixed for it.
 */
export const getTextSelectionStylesSummary = (fabricTextbox: Textbox, baseStyles: TextStyles, itemId: string, itemShadow: SerializedShadowOptions | null = null): TextSelectionStylesSummary => {
  const declarations: TextStyleDeclaration[] = fabricTextbox.getStylesForSelection();
  const baseSolidFillColor = baseStyles.fill.fillType === FillTypes.SOLID ? baseStyles.fill.fillColor[0] : null;
  const baseFillGradient: TextRunFillGradientObject | null =
    baseStyles.fill.fillType === FillTypes.LINEAR_GRADIENT || baseStyles.fill.fillType === FillTypes.RADIAL_GRADIENT ? {fillType: baseStyles.fill.fillType, fillColor: cloneDeep(baseStyles.fill.fillColor)} : null;
  const baseOutline: TextRunOutlineObject = {isEnabled: baseStyles.stroke, color: baseStyles.strokeColor, width: baseStyles.strokeWidth};
  const effectiveStyles = declarations.map((declaration) => {
    const passenger = getRunStylesPassenger(declaration);
    return {
      fontSize: passenger?.fontSize ?? baseStyles.fontSize,
      isBold: passenger?.isBold ?? baseStyles.isBold,
      isItalic: passenger?.isItalic ?? baseStyles.isItalic,
      underLine: passenger?.underLine ?? baseStyles.underLine,
      lineThrough: passenger?.lineThrough ?? baseStyles.lineThrough,
      fontFamily: passenger?.fontFamily ?? baseStyles.fontFamily,
      fillColor: passenger?.fillGradient ? null : (passenger?.fillColor ?? baseSolidFillColor),
      fillGradient: passenger?.fillGradient ?? (passenger?.fillColor ? null : baseFillGradient),
      outline: passenger?.outline ?? baseOutline,
      shadow: passenger?.shadow ?? itemShadow,
    };
  });

  const getUniformValue = <T extends number | boolean | string>(values: T[]): T | null => {
    return values.every((value) => value === values[0]) ? values[0] : null;
  };

  const getUniformValueDeep = <T>(values: (T | null)[]): T | null => {
    return values.every((value) => isEqual(value, values[0])) ? values[0] : null;
  };

  return {
    itemId,
    isSelectionCollapsed: fabricTextbox.selectionStart === fabricTextbox.selectionEnd,
    fontSize: getUniformValue(effectiveStyles.map((style) => style.fontSize)),
    isBold: getUniformValue(effectiveStyles.map((style) => style.isBold)),
    isItalic: getUniformValue(effectiveStyles.map((style) => style.isItalic)),
    underLine: getUniformValue(effectiveStyles.map((style) => style.underLine)),
    lineThrough: getUniformValue(effectiveStyles.map((style) => style.lineThrough)),
    fontFamily: getUniformValue(effectiveStyles.map((style) => style.fontFamily)),
    fillColor: getUniformValueDeep(effectiveStyles.map((style) => style.fillColor)),
    fillGradient: getUniformValueDeep(effectiveStyles.map((style) => style.fillGradient)),
    outline: getUniformValueDeep(effectiveStyles.map((style) => style.outline)),
    shadow: getUniformValueDeep(effectiveStyles.map((style) => style.shadow)),
    textAlign: getSelectionEffectiveTextAlign(fabricTextbox, baseStyles),
    lineList: getSelectionUniformLineList(fabricTextbox),
  };
};

/** The uniform list across the real lines the selection touches, or null when mixed / none. */
const getSelectionUniformLineList = (fabricTextbox: Textbox): TextLineListObject | null => {
  const lineStyles = harvestLineStylesFromFabricTextbox(fabricTextbox);
  const firstLine = fabricTextbox.get2DCursorLocation(fabricTextbox.selectionStart, true).lineIndex;
  const lastLine = fabricTextbox.get2DCursorLocation(Math.max(fabricTextbox.selectionStart, fabricTextbox.selectionEnd - (fabricTextbox.selectionEnd > fabricTextbox.selectionStart ? 1 : 0)), true).lineIndex;
  const firstList = lineStyles[firstLine]?.list ?? null;
  if (firstList === null) {
    return null;
  }
  for (let lineIndex = firstLine + 1; lineIndex <= lastLine; lineIndex++) {
    const list = lineStyles[lineIndex]?.list;
    if (list?.type !== firstList.type || list.style !== firstList.style) {
      return null;
    }
  }
  return firstList;
};

/** The uniform effective alignment across the real lines the selection touches, or null when mixed. */
const getSelectionEffectiveTextAlign = (fabricTextbox: Textbox, baseStyles: TextStyles): TextHorizontalAlignType | null => {
  const selectionStart = fabricTextbox.selectionStart;
  const selectionEnd = fabricTextbox.selectionEnd;
  const firstLine = fabricTextbox.get2DCursorLocation(selectionStart, true).lineIndex;
  const lastLine = fabricTextbox.get2DCursorLocation(Math.max(selectionStart, selectionEnd - (selectionEnd > selectionStart ? 1 : 0)), true).lineIndex;
  const alignments: TextHorizontalAlignType[] = [];
  for (let lineIndex = firstLine; lineIndex <= lastLine; lineIndex++) {
    const passenger = getLineStylesPassenger(fabricTextbox.lineStyles?.[lineIndex]);
    alignments.push(passenger?.textAlign ?? baseStyles.textAlign);
  }
  return alignments.every((alignment) => alignment === alignments[0]) ? alignments[0] : null;
};

/**
 * A collapsed caret styles nothing yet; what typing there will inherit is the character before
 * the caret (fabric's own typing inheritance), or the box style at the very start.
 */
const getDeclarationsTypingAtCaretInherits = (fabricTextbox: Textbox, caretIndex: number): TextStyleDeclaration[] => {
  if (caretIndex === 0) {
    return [{}];
  }
  const inherited: TextStyleDeclaration[] = fabricTextbox.getSelectionStyles(caretIndex - 1, caretIndex);
  return inherited.length > 0 ? inherited : [{}];
};

/**
 * The font-family variations the selection would render with after merging the partial in —
 * to be loaded before compiling, so styled glyphs measure with their real font instead of a
 * fallback. Empty when the partial touches no font-affecting property.
 */
export const getFontVariationsForSelectionApply = (
  fabricTextbox: Textbox,
  stylesPartial: Partial<TextRunStylesObject>,
  baseStyles: TextStyles,
  selectionStart: number,
  selectionEnd: number
): string[] => {
  if (stylesPartial.fontFamily === undefined && stylesPartial.isBold === undefined && stylesPartial.isItalic === undefined) {
    return [];
  }
  const declarations: TextStyleDeclaration[] = selectionStart === selectionEnd ? getDeclarationsTypingAtCaretInherits(fabricTextbox, selectionStart) : fabricTextbox.getSelectionStyles(selectionStart, selectionEnd);
  const fontVariations: string[] = [];
  for (const declaration of declarations) {
    const merged = {...EMPTY_TEXT_RUN_STYLES, ...(getRunStylesPassenger(declaration) ?? {}), ...stylesPartial};
    const fontVariation = getFontFamilyNameForVariations(merged.fontFamily ?? baseStyles.fontFamily, merged.isBold ?? baseStyles.isBold, merged.isItalic ?? baseStyles.isItalic);
    if (!fontVariations.includes(fontVariation)) {
      fontVariations.push(fontVariation);
    }
  }
  return fontVariations;
};

/**
 * Resolves once every font variation the apply needs is loaded, with the variations that were
 * still loading when it was called — the ones any text measured in the meantime got wrong.
 */
export const ensureSelectionApplyFontsAreLoaded = async (fabricTextbox: Textbox, stylesPartial: Partial<TextRunStylesObject>, baseStyles: TextStyles): Promise<string[]> => {
  const fontVariations = getFontVariationsForSelectionApply(fabricTextbox, stylesPartial, baseStyles, fabricTextbox.selectionStart, fabricTextbox.selectionEnd);
  if (fontVariations.length === 0) {
    return [];
  }
  const fontVariationsStillLoading = fontVariations.filter((fontVariation) => !isFontLoaded(fontVariation));
  await new Promise<void>((resolve, reject) => {
    addFonts(fontVariations, resolve, reject);
  });
  return fontVariationsStillLoading;
};

/**
 * Fabric caches every character width it measures per font family for the life of the page,
 * so glyphs measured while a face was still loading keep the fallback face's widths after the
 * real face arrives — the text draws wider than the box that was sized from those widths.
 * Dropping the cache for the families that just loaded and re-laying the textbox out replaces
 * the stale widths with the loaded face's.
 */
export const remeasureTextboxForLoadedFonts = (fabricTextbox: Textbox, loadedFontFamilies: string[]): void => {
  if (loadedFontFamilies.length === 0) {
    return;
  }
  for (const fontFamily of loadedFontFamilies) {
    // declarations carry the quoted family (getFontFamilyString) and fabric keys its cache on that
    cache.clearFontCache(fontFamily);
    cache.clearFontCache(getFontFamilyString(fontFamily));
  }
  fabricTextbox.initDimensions();
};

/**
 * The effective font size of every grapheme in [selectionStart, selectionEnd) — the baseline a
 * relative size drag scales from.
 */
export const getEffectiveFontSizesForSelection = (fabricTextbox: Textbox, baseStyles: TextStyles, selectionStart: number, selectionEnd: number): number[] => {
  const declarations: TextStyleDeclaration[] = fabricTextbox.getSelectionStyles(selectionStart, selectionEnd);
  return declarations.map((declaration) => getRunStylesPassenger(declaration)?.fontSize ?? baseStyles.fontSize);
};

const stylesMapHasRunDeclarations = (fabricTextbox: Textbox): boolean => {
  return Object.values(fabricTextbox.styles).some((lineDeclarations) => Object.values(lineDeclarations).some((declaration) => 'pmwRunStyles' in declaration));
};

const getRunStylesPassenger = (declaration: TextStyleDeclaration): TextRunStylesObject | null => {
  const passenger: unknown = declaration.pmwRunStyles;
  if (typeof passenger === 'object' && passenger !== null && 'fontSize' in passenger && 'isBold' in passenger && 'fillColor' in passenger) {
    return passenger as TextRunStylesObject;
  }
  return null;
};
